import { ZOOM_OPTIONS, FRAME_OPTIONS } from "../data/screenPolish";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api, { errMsg, csrfHeaders, fileUrl, fileKey } from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Upload, Mic, Square, ArrowRight, X, Loader2, Sparkles, Image as ImageIcon, Wand2, LayoutTemplate, Video } from "lucide-react";
import ScreenRecorder from "../components/ScreenRecorder";
import ScreenshotAnnotator from "../components/ScreenshotAnnotator";
import ScreenshotReorder from "../components/ScreenshotReorder";
import ClonedVoicePicker from "../components/ClonedVoicePicker";
import { useConfig } from "../lib/config";

const TONES = ["professional","friendly","energetic","instructional","enthusiastic","reverent"];

// Fallback voice list when the /providers/stock-voices call hasn't returned yet.
const FALLBACK_OPENAI_VOICES = [
  { id: "alloy", name: "Alloy" }, { id: "ash", name: "Ash" }, { id: "coral", name: "Coral" },
  { id: "echo", name: "Echo" }, { id: "fable", name: "Fable" }, { id: "nova", name: "Nova" },
  { id: "onyx", name: "Onyx" }, { id: "sage", name: "Sage" }, { id: "shimmer", name: "Shimmer" },
];
const FALLBACK_LANGS = [
  { code: "en", label: "English", region: "Global" },
  { code: "en-IN", label: "English (India)", region: "Indic" },
  { code: "hi-IN", label: "Hindi", region: "Indic" },
  { code: "ta-IN", label: "Tamil", region: "Indic" },
  { code: "te-IN", label: "Telugu", region: "Indic" },
  { code: "bn-IN", label: "Bengali", region: "Indic" },
  { code: "mr-IN", label: "Marathi", region: "Indic" },
  { code: "kn-IN", label: "Kannada", region: "Indic" },
  { code: "ml-IN", label: "Malayalam", region: "Indic" },
  { code: "gu-IN", label: "Gujarati", region: "Indic" },
  { code: "pa-IN", label: "Punjabi", region: "Indic" },
  { code: "or-IN", label: "Odia", region: "Indic" },
];

export default function NewProject() {
  const { featureEnabled } = useConfig();
  // Each clone provider has its own switch and they ship independently:
  // Pravaani is live, ElevenLabs and Muapi are not. Gating the whole panel on
  // `voice_cloning` alone would have hidden the one provider that works.
  const cloningAvailable = featureEnabled("voice_cloning") || featureEnabled("voice_cloning_pravaani");
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [projectId, setProjectId] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [screenshotAnnotations, setScreenshotAnnotations] = useState({});
  const [selectedShotIdx, setSelectedShotIdx] = useState(0);
  const [savingAnnotations, setSavingAnnotations] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [providers, setProviders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [languages, setLanguages] = useState(FALLBACK_LANGS);
  const [ttsCatalog, setTtsCatalog] = useState({
    openai: FALLBACK_OPENAI_VOICES,
    sarvam: [],
    elevenlabs: [],
    edge: [],
    muapi: [],
    pravaani: [],
  });
  const [muapiServerKey, setMuapiServerKey] = useState(false);
  const [voiceProvider, setVoiceProvider] = useState("openai"); // openai | sarvam | elevenlabs | edge | muapi | pravaani
  // Whether the user has actually chosen a voice provider. Without this the
  // Indic nudge below cannot tell "still on the default" from "picked this on
  // purpose", and overrides both.
  const [voiceProviderPicked, setVoiceProviderPicked] = useState(false);
  const [cloneVoice, setCloneVoice] = useState(false); // toggle in step 2
  // Pravaani mints a voiceprint we then store, so the backend refuses to
  // enroll without a named subject and authoriser. Captured here rather than
  // assumed, because "it is my own voice" is a claim only the user can make.
  const [cloneConsent, setCloneConsent] = useState(false);
  const [cloneSubject, setCloneSubject] = useState("");
  const [cloneInfo, setCloneInfo] = useState(null);    // { voice_id, name, provider, preview_url }
  const [cloning, setCloning] = useState(false);
  // Which cloning backends can work here, from /clone-providers, and the pick.
  const [cloneProviders, setCloneProviders] = useState([]);
  const [cloneProvider, setCloneProvider] = useState("");
  const [form, setForm] = useState({
    title:"", kind:"both", description:"", tone:"professional",
    language:"en", voice:"nova", voiceover_mode:"tts", caption_style:"sidecar",
    bg_music_key:"", bg_music_volume:0.15,
    video_style:"slideshow", aspect_ratio:"16:9", broll_mode:"off", duration_target:60,
    llm_provider_id:"", tts_provider_id:"", stt_provider_id:"", text_input:"",
    team_id:"", source_type:"slideshow", screen_audio_mode:"keep_recording",
    // New recordings opt in; the model defaults stay off so existing projects
    // do not change on their next re-render.
    screen_zoom:"normal", screen_frame:"midnight",
  });
  const [screenBlob, setScreenBlob] = useState(null);
  const [screenType, setScreenType] = useState(null);
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicPreviewKey, setMusicPreviewKey] = useState(null);
  const musicAudioRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(true);

  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const [recording, setRecording] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  const streamPreview = async () => {
    if (!projectId) return;
    setStreamText(""); setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/stream-script`, {
        method: "POST",
        credentials: "include",
        headers: csrfHeaders(),
      });
      const reader = res.body.getReader(); const dec = new TextDecoder();
      let buffer = ""; let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const lines = buffer.split("\n\n"); buffer = lines.pop() || "";
        for (const line of lines) {
          const m = line.match(/^data: (.*)$/s);
          if (!m) continue;
          const chunk = m[1];
          if (chunk === "[DONE]") { setStreaming(false); return; }
          if (chunk.startsWith("[ERROR]")) { toast.error(chunk); setStreaming(false); return; }
          acc += chunk;
          setStreamText(acc);
        }
      }
    } catch(e){ toast.error("Stream failed"); }
    finally { setStreaming(false); }
  };

  const saveEditedScript = async () => {
    if (!projectId || !streamText.trim()) return;
    try {
      await api.patch(`/projects/${projectId}`, { script: streamText });
      toast.success("Script saved — will be used verbatim on generate");
    } catch(e){ toast.error("Save failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file || !projectId) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/projects/${projectId}/brand-kit`, fd, { headers:{"Content-Type":"multipart/form-data"}});
      toast.success("Brand logo attached");
    } catch(e){ toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  useEffect(()=>{
    api.get("/providers").then(r=>setProviders(r.data)).catch(()=>{});
    api.get("/teams").then(r=>setTeams(r.data.filter(t=>t.role!=="viewer"))).catch(()=>{});
    api.get("/providers/languages").then(r=>{ if (r.data?.languages?.length) setLanguages(r.data.languages); }).catch(()=>{});
    api.get("/templates").then(r=>setTemplates(r.data?.templates || [])).catch(()=>{});
    api.get("/music/library").then(r=>setMusicTracks(r.data?.tracks || [])).catch(()=>{});
    // Load stock voices for each kind up-front (fast — served from constants).
    Promise.all([
      api.get("/providers/stock-voices?kind=sarvam").catch(()=>({data:{voices:[]}})),
      api.get("/providers/stock-voices?kind=elevenlabs").catch(()=>({data:{voices:[]}})),
      api.get("/providers/stock-voices?kind=openai").catch(()=>({data:{voices:FALLBACK_OPENAI_VOICES}})),
      api.get("/providers/stock-voices?kind=edge").catch(()=>({data:{voices:[]}})),
      api.get("/providers/stock-voices?kind=muapi").catch(()=>({data:{voices:[], configured:false}})),
      api.get("/providers/stock-voices?kind=pravaani").catch(()=>({data:{voices:[]}})),
    ]).then(([sv, ev, ov, edg, mu, pv])=>{
      setTtsCatalog({
        sarvam: sv.data?.voices || [],
        elevenlabs: ev.data?.voices || [],
        openai: ov.data?.voices || FALLBACK_OPENAI_VOICES,
        edge: edg.data?.voices || [],
        muapi: mu.data?.voices || [],
        pravaani: pv.data?.voices || [],
      });
      setMuapiServerKey(!!mu.data?.configured);
    });
  },[]);

  // MiniMax voices need a Muapi key: the server's, or the user's own provider.
  // Offering the option without either would only fail at render time.
  const muapiProvider = providers.find((p) => p.kind === "muapi" && p.type === "tts");
  const muapiAvailable = muapiServerKey || !!muapiProvider;
  // Pravaani speaks through a RunPod endpoint, so it is only offerable when
  // the user has registered one. Without this the voice list and the TTS
  // engine could disagree: the dropdown showed OpenAI's "Nova" while a
  // Pravaani TTS provider actually narrated, and Nova was silently discarded.
  const pravaaniProvider = providers.find((p) => p.kind === "pravaani" && p.type === "tts");
  const pravaaniAvailable = !!pravaaniProvider;
  const isIndicLang = (form.language || "").endsWith("-IN") && form.language !== "en-IN";
  // Indic languages get Sarvam as a DEFAULT, not an override. This used to
  // force "sarvam" for any Indic language unless the provider happened to be
  // "muapi" -- an exemption added by hand when Muapi arrived. Pravaani was
  // added without one, so choosing it for Telugu snapped straight back to
  // Sarvam and the option looked broken. A hand-maintained exemption list is
  // wrong once per new provider; respecting an explicit choice is never wrong.
  const suggestedProvider = (!voiceProviderPicked && isIndicLang) ? "sarvam" : voiceProvider;
  const voiceList = ttsCatalog[suggestedProvider] || FALLBACK_OPENAI_VOICES;

  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  const applyTemplate = (t) => {
    setPickedTemplate(t.id);
    setForm(f => ({
      ...f,
      ...(t.defaults || {}),
      // Persist WHICH template was used, not just its defaults. `pickedTemplate`
      // is local state that dies with the wizard, so template_id was never sent
      // and the backend could not tell which template produced a project — no
      // way to see which ones convert, or to improve them.
      template_id: t.id,
      description: f.description || (t.description_hint || ""),
    }));
    setShowTemplates(false);
    toast.success(`Template applied: ${t.label}`);
  };

  const skipTemplates = () => {
    setPickedTemplate(null);
    setForm(f => ({ ...f, template_id: null }));
    setShowTemplates(false);
  };

  const createProject = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      // strip empty ids
      ["llm_provider_id","tts_provider_id","stt_provider_id","team_id","bg_music_key","template_id"].forEach(k=>{ if(!payload[k]) delete payload[k]; });
      const r = await api.post("/projects", payload);
      setProjectId(r.data.project_id);
      setStep(2);
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  // `order` is indices into the current list — screenshot URLs are presigned
  // and regenerated on every read, so they cannot be used as stable ids.
  const reorderShots = async (order, movedTo) => {
    const optimistic = order.map((i) => screenshots[i]);
    const previous = screenshots;
    setScreenshots(optimistic);          // reflow immediately; the grid is the control
    setSelectedShotIdx(movedTo);
    setReordering(true);
    try {
      await api.put(`/projects/${projectId}/screenshots/order`, { order });
    } catch (e) {
      setScreenshots(previous);          // put it back rather than lie about the order
      setSelectedShotIdx(selectedShotIdx);
      toast.error(errMsg(e, "Could not save the new slide order"));
    } finally {
      setReordering(false);
    }
  };

  const deleteShot = async (index) => {
    const previous = screenshots;
    setScreenshots((cur) => cur.filter((_, i) => i !== index));
    setSelectedShotIdx((cur) => Math.max(0, cur >= index ? cur - 1 : cur));
    setReordering(true);
    try {
      await api.delete(`/projects/${projectId}/screenshots/${index}`);
    } catch (e) {
      setScreenshots(previous);
      toast.error(errMsg(e, "Could not remove that screenshot"));
    } finally { setReordering(false); }
  };

  const onShots = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f=>fd.append("files", f));
      const r = await api.post(`/projects/${projectId}/screenshots`, fd, { headers:{"Content-Type":"multipart/form-data"}});
      setScreenshots(r.data.screenshots);
      setSelectedShotIdx(Math.max(0, (r.data.screenshots?.length || 1) - 1));
    } catch(e){ toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e)=>chunks.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunks.current, {type:"audio/webm"});
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t=>t.stop());
      };
      mediaRec.current = rec;
      rec.start();
      setRecording(true);
    } catch(e){ toast.error("Microphone access denied"); }
  };
  const stopRec = () => { mediaRec.current?.stop(); setRecording(false); };

  const uploadAudio = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", audioBlob, "recording.webm");
      await api.post(`/projects/${projectId}/audio`, fd, { headers:{"Content-Type":"multipart/form-data"}});
      toast.success("Voice memo attached");
    } catch(e){ toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  useEffect(() => {
    if (!cloneVoice || !projectId) return;
    api.get(`/projects/${projectId}/clone-providers`).then((r) => {
      const list = r.data?.providers || [];
      setCloneProviders(list);
      // Default only to something that can work. Falling back to the first
      // entry made an unavailable provider render as the selected one.
      setCloneProvider((cur) => (list.find((c) => c.id === cur && c.available) ? cur
        : (list.find((c) => c.available) || {}).id || ""));
    }).catch(() => setCloneProviders([]));
  }, [cloneVoice, projectId]);

  const pickedClone = cloneProviders.find((c) => c.id === cloneProvider);
  const cloneBlockedReason = pickedClone && !pickedClone.available ? pickedClone.reason : null;

  const cloneVoiceNow = async () => {
    if (!audioBlob) { toast.error("Record or upload audio first"); return; }
    setCloning(true);
    try {
      // Ensure audio is uploaded so the backend can read the sample.
      await uploadAudio();
      const fd = new FormData();
      fd.append("file", audioBlob, audioBlob.name || "sample.webm");
      const provider = cloneProvider || "elevenlabs";
      fd.append("provider", provider);
      fd.append("name", form.title || "MyVoice");
      const subject = (cloneSubject || "").trim() || "self";
      const r = await api.post(`/projects/${projectId}/clone-voice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { provider, name: form.title || "MyVoice",
                  consent_subject: subject },
      });
      setCloneInfo({ voice_id: r.data.voice_id, name: r.data.name, provider: r.data.provider,
                     preview_url: r.data.preview_url });
      // A MiniMax clone must be spoken with the key that trained it, so the
      // backend may have pointed the project's TTS provider at the user's
      // Muapi provider. Mirror it here: the wizard re-sends tts_provider_id
      // from this form when generation starts, and would otherwise undo it.
      if (r.data.tts_provider_id) update("tts_provider_id", r.data.tts_provider_id);
      toast.success(`Voice cloned as "${r.data.name}"`);
    } catch (err) {
      toast.error(errMsg(err, "Voice cloning failed"));
    } finally { setCloning(false); }
  };

  const uploadScreenRecording = async () => {
    if (!screenBlob || !projectId) return;
    const fd = new FormData();
    const filename = `screen.${(screenType || "video/webm").includes("mp4") ? "mp4" : "webm"}`;
    fd.append("file", new File([screenBlob], filename, { type: screenType || "video/webm" }));
    setUploading(true);
    try {
      await api.post(`/projects/${projectId}/screen-recording`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success("Recording uploaded");
    } catch(e){ toast.error(errMsg(e, "Recording upload failed")); throw e; }
    finally { setUploading(false); }
  };

  const saveAnnotations = async () => {
    if (!projectId) return;
    setSavingAnnotations(true);
    try {
      await api.put(`/projects/${projectId}/annotations`, { annotations: screenshotAnnotations });
      toast.success("Annotations saved");
    } catch (e) {
      toast.error(errMsg(e, "Failed to save annotations"));
      throw e;
    } finally {
      setSavingAnnotations(false);
    }
  };

  // Steps 2-3 mutate `form` locally, but the project row was written back on
  // step 1 — so every choice made after that (audio mode, captions, music,
  // typed notes) exists only in React state. Persist the form before handing
  // off to the worker, or the render uses the step-1 snapshot.
  const persistSettings = async () => {
    if (!projectId) return;
    const payload = { ...form };
    ["llm_provider_id","tts_provider_id","stt_provider_id","team_id","bg_music_key"]
      .forEach(k => { if (!payload[k]) delete payload[k]; });
    await api.patch(`/projects/${projectId}`, payload);
  };

  const startGeneration = async () => {
    try {
      await persistSettings();
      if (form.source_type === "screen_recording") {
        if (!screenBlob) { toast.error("Record your screen first"); return; }
        await uploadScreenRecording();
      } else {
        if (audioBlob) await uploadAudio();
        if (Object.keys(screenshotAnnotations).length > 0) {
          await saveAnnotations();
        }
      }
      await api.post(`/projects/${projectId}/generate`);
      nav(`/project/${projectId}`);
    } catch(e){ toast.error(errMsg(e, "Failed to start")); }
  };

  const providersOf = (t) => providers.filter(p=>p.type===t);

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <NoticeRegion />
        <div className="marquee-tag mb-2">// NEW PROJECT</div>
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-display font-black type-title tracking-tighter">STEP {step} / 3</h1>
          <div className="text-xs uppercase tracking-widest text-ink-muted">
            {step===1?"Configure":step===2?"Add assets":"Review & generate"}
          </div>
        </div>

        {step===1 && showTemplates && (
          <div className="brutal-card space-y-4 mb-8" data-testid="template-picker">
            <div className="flex items-center justify-between">
              <div>
                <div className="marquee-tag mb-1">// START FROM A TEMPLATE</div>
                <div className="text-xs uppercase tracking-widest text-ink-muted">
                  Pick a preset to skip the boring bits — you can still tweak everything.
                </div>
              </div>
              <button data-testid="btn-skip-templates" onClick={skipTemplates} className="brutal-btn brutal-btn-ghost">
                Start blank <ArrowRight size={14}/>
              </button>
            </div>
            {templates.length === 0 ? (
              <div className="text-sm text-ink-muted italic">Loading templates…</div>
            ) : (
              <div className="grid md:grid-cols-3 gap-3">
                {templates.map(t=>(
                  <button
                    key={t.id}
                    onClick={()=>applyTemplate(t)}
                    data-testid={`template-card-${t.id}`}
                    className="brutal-border p-4 text-left hover:bg-surface transition-colors"
                    style={{ borderLeft: `6px solid ${t.accent || "#050505"}` }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl leading-none">{t.emoji}</span>
                      <span className="font-display font-black text-sm tracking-tight uppercase">{t.label}</span>
                    </div>
                    <div className="text-xs leading-relaxed text-ink-muted">{t.blurb}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-2xs uppercase tracking-widest text-ink-subtle bg-surface px-1.5 py-0.5">{t.defaults?.tone}</span>
                      <span className="text-2xs uppercase tracking-widest text-ink-subtle bg-surface px-1.5 py-0.5">{t.defaults?.duration_target}s</span>
                      <span className="text-2xs uppercase tracking-widest text-ink-subtle bg-surface px-1.5 py-0.5">{t.defaults?.language}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step===1 && (
          <div className="brutal-card space-y-4" data-testid="step1-form">
            {pickedTemplate && (
              <div className="flex items-center justify-between bg-surface brutal-border px-3 py-2" data-testid="template-applied-chip">
                <div className="text-2xs uppercase tracking-widest">
                  Using template: <span className="font-black">{templates.find(t=>t.id===pickedTemplate)?.label}</span>
                </div>
                <button
                  onClick={()=>{ setPickedTemplate(null); setShowTemplates(true); }}
                  className="text-2xs uppercase tracking-widest underline"
                  data-testid="btn-change-template"
                >Change template</button>
              </div>
            )}
            <div>
              <label className="brutal-label" htmlFor="input-title">Title</label>
              <input id="input-title" data-testid="input-title" className="brutal-input" value={form.title} onChange={(e)=>update("title",e.target.value)} placeholder="e.g. Acme Dashboard Onboarding"/>
            </div>
            <div>
              <label className="brutal-label" htmlFor="input-description">Description / notes for the AI</label>
              <textarea id="input-description" data-testid="input-description" className="brutal-input min-h-[100px]" value={form.description} onChange={(e)=>update("description",e.target.value)} placeholder="What does your product do? Key features to highlight?"/>
            </div>

            <fieldset className="min-w-0 border-0 p-0 m-0" data-testid="source-type-toggle">
              <legend className="brutal-label p-0">How are you creating this demo?</legend>
              <div className="grid md:grid-cols-2 gap-2">
                <label
                  className={`brutal-border p-3 cursor-pointer text-left ${form.source_type==="slideshow" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}
                  data-testid="source-slideshow"
                >
                  <input
                    type="radio"
                    name="source_type"
                    value="slideshow"
                    checked={form.source_type==="slideshow"}
                    onChange={()=>update("source_type","slideshow")}
                    className="mr-2 align-middle"
                  />
                  <span className="font-display font-black text-sm uppercase inline-flex items-center gap-1"><ImageIcon size={14}/> Screenshots + narration</span>
                  <div className="text-xs mt-1 leading-relaxed opacity-80">
                    Upload screenshots + record a voice memo. We compose the slideshow, script, and manual.
                  </div>
                </label>
                <label
                  className={`brutal-border p-3 cursor-pointer text-left ${form.source_type==="screen_recording" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}
                  data-testid="source-screen-recording"
                >
                  <input
                    type="radio"
                    name="source_type"
                    value="screen_recording"
                    checked={form.source_type==="screen_recording"}
                    onChange={()=>update("source_type","screen_recording")}
                    className="mr-2 align-middle"
                  />
                  <span className="font-display font-black text-sm uppercase inline-flex items-center gap-1"><Video size={14}/> Record my screen <span className="brutal-chip !bg-brand !text-white text-2xs ml-1">NEW</span></span>
                  <div className="text-xs mt-1 leading-relaxed opacity-80">
                    Capture your app + narration in one take. We transcode, add captions, and build a manual.
                  </div>
                </label>
              </div>
            </fieldset>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="brutal-label" htmlFor="select-kind">Output</label>
                <select id="select-kind" data-testid="select-kind" className="brutal-select" value={form.kind} onChange={(e)=>update("kind",e.target.value)}>
                  <option value="both">Video + Manual</option>
                  <option value="demo_video">Demo Video only</option>
                  <option value="product_manual">Manual only</option>
                </select>
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-tone">Tone</label>
                <select id="select-tone" data-testid="select-tone" className="brutal-select" value={form.tone} onChange={(e)=>update("tone",e.target.value)}>
                  {TONES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="brutal-label" htmlFor="input-duration">Duration (sec)</label>
                <input id="input-duration" data-testid="input-duration" type="number" min="15" max="600" className="brutal-input" value={form.duration_target} onChange={(e)=>update("duration_target",parseInt(e.target.value||"60"))}/>
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-voice-provider">Voice Provider</label>
                <select id="select-voice-provider" data-testid="select-voice-provider" className="brutal-select" value={suggestedProvider} onChange={(e)=>{
                  setVoiceProvider(e.target.value);
                  setVoiceProviderPicked(true);
                  const first = (ttsCatalog[e.target.value] || [])[0];
                  if (first) update("voice", first.id);
                  // Without a server key, MiniMax voices can only be reached
                  // through the user's own Muapi provider, so select it.
                  if (e.target.value === "muapi" && !muapiServerKey && muapiProvider && !form.tts_provider_id) {
                    update("tts_provider_id", muapiProvider.provider_id);
                  }
                  // A Pravaani voice is only resolvable on a Pravaani endpoint.
                  if (e.target.value === "pravaani" && pravaaniProvider) {
                    update("tts_provider_id", pravaaniProvider.provider_id);
                  }
                }}>
                  <option value="edge">Edge TTS (free)</option>
                  <option value="openai">OpenAI (English &amp; broad)</option>
                  <option value="sarvam">Sarvam Bulbul (Indic native)</option>
                  <option value="elevenlabs">ElevenLabs (multilingual)</option>
                  {muapiAvailable && <option value="muapi">MiniMax via Muapi (expressive)</option>}
                  {pravaaniAvailable && <option value="pravaani">Pravaani OmniVoice (Indic + your clones)</option>}
                </select>
                {isIndicLang && (suggestedProvider === "openai" || suggestedProvider === "edge") && (
                  <p className="text-xs uppercase tracking-widest text-ink-accent mt-1">Tip: Sarvam{pravaaniAvailable ? ", Pravaani" : ""} or ElevenLabs give native {form.language} narration.</p>
                )}
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-voice">Voice</label>
                <select id="select-voice" data-testid="select-voice" className="brutal-select" value={form.voice} onChange={(e)=>update("voice",e.target.value)}>
                  {voiceList.length === 0 && <option value="">(no voices)</option>}
                  {voiceList.map(v=><option key={v.id} value={v.id}>{v.name || v.id}</option>)}
                </select>
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-video-style">Video Style</label>
                <select id="select-video-style" data-testid="select-video-style" className="brutal-select" value={form.video_style} onChange={(e)=>update("video_style",e.target.value)}>
                  <option value="slideshow">Slideshow (fast)</option>
                  <option value="sora">Sora 2 AI (slower)</option>
                </select>
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-aspect-ratio">Aspect Ratio</label>
                <select id="select-aspect-ratio" data-testid="select-aspect-ratio" className="brutal-select" value={form.aspect_ratio} onChange={(e)=>update("aspect_ratio",e.target.value)}>
                  <option value="16:9">16:9 — Landscape</option>
                  <option value="9:16">9:16 — Vertical (Reels/Shorts/TikTok)</option>
                  <option value="1:1">1:1 — Square</option>
                </select>
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-broll-mode">Stock B-roll</label>
                <select id="select-broll-mode" data-testid="select-broll-mode" className="brutal-select" value={form.broll_mode} onChange={(e)=>update("broll_mode",e.target.value)}>
                  {form.source_type === "screen_recording" ? (
                    <>
                      <option value="off">Off</option>
                      <option value="stock">On — stock cutaways over quiet stretches</option>
                    </>
                  ) : (
                    <>
                      <option value="off">Off — screenshots only</option>
                      <option value="stock">On — free Pexels/Pixabay clips</option>
                    </>
                  )}
                </select>
                {form.source_type === "screen_recording" ? (
                  <p className="text-xs uppercase tracking-widest text-ink-subtle mt-1">Stock clips replace the picture during pauses; narration and captions keep running.</p>
                ) : form.broll_mode==="stock" && (
                  <p className="text-xs uppercase tracking-widest text-ink-subtle mt-1">Free B-roll clips are matched to your script and woven between slides.</p>
                )}
              </div>
              <div>
                <label className="brutal-label" htmlFor="select-language">Language</label>
                <select id="select-language" data-testid="select-language" className="brutal-select" value={form.language} onChange={(e)=>update("language",e.target.value)}>
                  {["Global","Indic"].map(region=>(
                    <optgroup key={region} label={region}>
                      {languages.filter(l=>l.region===region).map(l=>(
                        <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[["LLM","llm_provider_id","llm"],["TTS","tts_provider_id","tts"],["STT","stt_provider_id","stt"]].map(([lbl,k,t])=>(
                <div key={k}>
                  <label className="brutal-label" htmlFor="provider">{lbl} Provider</label>
                  <select id="provider" data-testid={`select-${t}-provider`} className="brutal-select" value={form[k]} onChange={(e)=>update(k,e.target.value)}>
                    <option value="">Default (Emergent)</option>
                    {providersOf(t).map(p=><option key={p.provider_id} value={p.provider_id}>{p.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div>
              <label className="brutal-label" htmlFor="select-team">Workspace</label>
              <select id="select-team" data-testid="select-team" className="brutal-select" value={form.team_id} onChange={(e)=>update("team_id",e.target.value)}>
                <option value="">Personal (only you)</option>
                {teams.map(t=>(
                  <option key={t.team_id} value={t.team_id}>{t.name} — shared with {t.role === "owner" ? "your team" : `team (${t.role})`}</option>
                ))}
              </select>
              <p className="text-xs uppercase tracking-widest text-ink-subtle mt-1">
                {teams.length ? "Assigning to a team makes this project visible to all team members." : "Create a team in /teams to collaborate on projects."}
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button data-testid="btn-step1-next" onClick={createProject} disabled={creating} className="brutal-btn">
                {creating?"Creating…":"Next"} <ArrowRight size={16}/>
              </button>
            </div>
          </div>
        )}

        {step===2 && (
          <div className="space-y-6" data-testid="step2-uploads">
            {form.source_type === "screen_recording" ? (
              <>
              <ScreenRecorder
                blob={screenBlob}
                onRecorded={(b, dur, type)=>{
                  setScreenBlob(b);
                  setScreenType(type);
                  if (dur > 0) update("duration_target", Math.max(15, Math.min(600, dur)));
                }}
              />
              <div className="brutal-card" data-testid="screen-audio-mode">
                <fieldset className="min-w-0 border-0 p-0 m-0">
                <legend className="marquee-tag mb-3">Audio</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className={`field-focusable brutal-border p-3 cursor-pointer text-left ${form.screen_audio_mode==="keep_recording" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`} data-testid="audio-mode-keep">
                    <input type="radio" name="screen_audio_mode" className="sr-only" checked={form.screen_audio_mode==="keep_recording"} onChange={()=>update("screen_audio_mode","keep_recording")}/>
                    <div className="font-display font-bold">Keep my recording</div>
                    <div className="text-xs uppercase tracking-widest opacity-70 mt-1">Use the mic audio as captured</div>
                  </label>
                  <label className={`field-focusable brutal-border p-3 cursor-pointer text-left ${form.screen_audio_mode==="ai_voice" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`} data-testid="audio-mode-ai">
                    <input type="radio" name="screen_audio_mode" className="sr-only" checked={form.screen_audio_mode==="ai_voice"} onChange={()=>update("screen_audio_mode","ai_voice")}/>
                    <div className="font-display font-bold">Re-narrate with AI voice</div>
                    <div className="text-xs uppercase tracking-widest opacity-70 mt-1">Clean up the transcript & replace audio with TTS</div>
                  </label>
                </div>
                </fieldset>
              </div>
              <div className="brutal-card" data-testid="screen-polish">
                <div className="marquee-tag mb-3">// POLISH</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="brutal-label" htmlFor="select-screen-zoom">Auto-zoom</label>
                    <select id="select-screen-zoom" data-testid="select-screen-zoom" className="brutal-select"
                      value={form.screen_zoom} onChange={(e)=>update("screen_zoom", e.target.value)}>
                      {ZOOM_OPTIONS.map((o)=><option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <p className="text-xs text-ink-muted mt-1">
                      Eases in on clicks and where the pointer rests. Works on recordings made with the
                      DemoCraft browser extension — a recording made here gets the frame only.
                    </p>
                  </div>
                  <fieldset className="min-w-0 border-0 p-0 m-0">
                    <legend className="brutal-label">Frame</legend>
                    <div className="flex flex-wrap gap-2" role="radiogroup">
                      {FRAME_OPTIONS.map((o)=>(
                        <label key={o.v} data-testid={`frame-${o.v}`}
                          className={`field-focusable brutal-border px-3 py-2 cursor-pointer text-xs uppercase tracking-widest flex items-center gap-2 ${form.screen_frame===o.v ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}>
                          <input type="radio" name="screen_frame" className="sr-only" value={o.v}
                            checked={form.screen_frame===o.v} onChange={()=>update("screen_frame", o.v)} />
                          <span aria-hidden="true" className="inline-block w-4 h-4 brutal-border"
                            style={o.swatch ? { background: `linear-gradient(135deg, ${o.swatch[0]}, ${o.swatch[1]})` } : { background: "repeating-linear-gradient(45deg,#fff,#fff 3px,#ddd 3px,#ddd 6px)" }} />
                          {o.l.split(" —")[0]}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-ink-muted mt-1">Pads, rounds and shadows the recording on a background.</p>
                  </fieldset>
                </div>
              </div>
              </>
            ) : (
            <>
            <div className="brutal-card">
              <div className="marquee-tag mb-3">// SCREENSHOTS</div>
              <label className="field-focusable block cursor-pointer" data-testid="upload-screenshots">
                <div className="brutal-border p-10 text-center border-dashed hover:bg-surface">
                  <Upload size={32} className="mx-auto mb-2"/>
                  <div className="font-display font-bold type-subheading">Drop screenshots here</div>
                  <div className="text-xs uppercase tracking-widest text-ink-muted mt-1">PNG · JPG · WEBP</div>
                </div>
                <input type="file" multiple accept="image/*" className="sr-only" onChange={(e)=>onShots(e.target.files)}/>
              </label>
              {uploading && <div className="flex items-center gap-2 mt-3 text-sm"><Loader2 className="animate-spin" size={14}/> Uploading…</div>}
              {screenshots.length>0 && (
                <div className="mt-4 space-y-4" data-testid="screenshots-preview">
                  <ScreenshotReorder
                    screenshots={screenshots.map(fileUrl)}
                    selectedIdx={selectedShotIdx}
                    onSelect={setSelectedShotIdx}
                    disabled={reordering}
                    onReorder={reorderShots}
                    onDelete={deleteShot}
                    onAdd={onShots}
                  />
                  {screenshots[selectedShotIdx] && (
                    <div>
                      <div className="marquee-tag mb-2">// HIGHLIGHTS & POINTERS · SLIDE {selectedShotIdx + 1}</div>
                      <ScreenshotAnnotator
                        screenshotUrl={fileUrl(screenshots[selectedShotIdx])}
                        annotations={screenshotAnnotations[fileKey(screenshots[selectedShotIdx])] || []}
                        onChange={(next) => {
                          // fileKey: the signed URL's query changes between reads.
                          const url = fileKey(screenshots[selectedShotIdx]);
                          setScreenshotAnnotations((prev) => {
                            const copy = { ...prev };
                            if (next.length) copy[url] = next;
                            else delete copy[url];
                            return copy;
                          });
                        }}
                        onSave={saveAnnotations}
                        saving={savingAnnotations}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="brutal-card">
              <div className="marquee-tag mb-3">// VOICE MEMO (OPTIONAL)</div>
              <div className="flex flex-wrap items-center gap-3">
                {!recording ? (
                  <button data-testid="btn-record" onClick={startRec} className="brutal-btn"><Mic size={16}/> Record</button>
                ) : (
                  <button data-testid="btn-stop-record" onClick={stopRec} className="brutal-btn"><Square size={16}/> Stop</button>
                )}
                <label className="field-focusable brutal-btn brutal-btn-secondary cursor-pointer" data-testid="btn-upload-audio">
                  <Upload size={16}/> Upload Audio
                  <input type="file" accept="audio/*" className="sr-only" onChange={(e)=>{
                    const f = e.target.files?.[0]; if(f){ setAudioBlob(f); setAudioUrl(URL.createObjectURL(f)); }
                  }}/>
                </label>
                {audioUrl && (
                  <div className="flex items-center gap-2">
                    <audio src={audioUrl} controls className="h-10"/>
                    <button onClick={()=>{setAudioBlob(null);setAudioUrl(null);setCloneInfo(null);setCloneVoice(false);update("voiceover_mode","tts");}} className="brutal-btn-ghost brutal-btn" data-testid="btn-clear-audio" aria-label="Remove uploaded audio"><X size={14}/></button>
                  </div>
                )}
              </div>
              {/* Outside the `audioBlob` gate on purpose: a saved voice is an
                  account asset, so offering it must not require recording a new
                  one first — which also left screen-recording projects with no
                  way to use a saved voice at creation time. */}
              {/* The other half of cloning: a voiceprint is account-level, so
                  the recording above is only one way to get one. Rendered
                  whether or not a clone already exists, because switching
                  voices through use-voice merely repoints the project —
                  whereas the "remove" button on the status card below
                  destroys the voiceprint. */}
              {cloningAvailable && (
                <ClonedVoicePicker
                  projectId={projectId}
                  value={cloneInfo ? {
                    cloned_voice_id: cloneInfo.voice_id,
                    cloned_voice_provider: cloneInfo.provider,
                    cloned_voice_name: cloneInfo.name,
                  } : null}
                  onChange={(next) => {
                    if (!next) { setCloneInfo(null); setCloneVoice(false); return; }
                    setCloneInfo({
                      voice_id: next.cloned_voice_id,
                      provider: next.cloned_voice_provider,
                      name: next.cloned_voice_name,
                      preview_url: null,
                    });
                    // Same reason the clone flow mirrors this: the wizard
                    // re-sends tts_provider_id from `form` on generate and
                    // would otherwise undo the server's repoint.
                    if (next.tts_provider_id) update("tts_provider_id", next.tts_provider_id);
                  }}
                />
              )}
              {audioBlob && (
                <div className="mt-5 pt-5 border-t brutal-border border-l-0 border-r-0 border-b-0 space-y-5">
                  <div data-testid="voiceover-mode-block">
                    <div className="marquee-tag mb-2">// VOICEOVER MODE</div>
                    <div className="grid md:grid-cols-2 gap-2">
                      <label className={`brutal-border p-3 cursor-pointer text-left ${form.voiceover_mode==="tts" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`} data-testid="vo-mode-tts">
                        <input
                          type="radio"
                          name="voiceover_mode"
                          value="tts"
                          checked={form.voiceover_mode==="tts"}
                          onChange={()=>update("voiceover_mode","tts")}
                          className="mr-2 align-middle"
                        />
                        <span className="font-display font-black text-sm uppercase">Generate AI voice</span>
                        <div className="text-xs mt-1 leading-relaxed opacity-80">
                          We'll write a script from your recording + notes and read it aloud in the voice you picked.
                        </div>
                      </label>
                      <label className={`brutal-border p-3 cursor-pointer text-left ${form.voiceover_mode==="raw" ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`} data-testid="vo-mode-raw">
                        <input
                          type="radio"
                          name="voiceover_mode"
                          value="raw"
                          checked={form.voiceover_mode==="raw"}
                          onChange={()=>update("voiceover_mode","raw")}
                          className="mr-2 align-middle"
                        />
                        <span className="font-display font-black text-sm uppercase">Use my voice as-is</span>
                        <div className="text-xs mt-1 leading-relaxed opacity-80">
                          Skip TTS entirely. The recording above becomes the video's voiceover. Fastest, most authentic.
                        </div>
                      </label>
                    </div>
                  </div>
                  {cloningAvailable && (
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="clone-voice-toggle-wrap">
                    <input
                      type="checkbox"
                      data-testid="clone-voice-toggle"
                      className="mt-1 w-4 h-4"
                      checked={cloneVoice}
                      onChange={(e)=>setCloneVoice(e.target.checked)}
                    />
                    <div>
                      <div className="font-display font-bold text-sm">Clone my voice for this project</div>
                      <div className="text-xs text-ink-muted leading-relaxed mt-1">
                        The recording above is sent to {pickedClone?.available ? pickedClone.label : "the voice-cloning provider you pick below"} and used to narrate the video.
                        30 seconds of clear speech is enough. Only you can hear the result — the voice is scoped to this project.
                        You must have permission to use this voice.
                      </div>
                    </div>
                  </label>
                  )}
                  {cloningAvailable && cloneVoice && !cloneInfo && cloneProviders.length > 0 && (
                    <fieldset className="mt-4" data-testid="clone-provider-picker">
                      <legend className="brutal-label">Clone with</legend>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {cloneProviders.map((c) => (
                          <label key={c.id}
                            className={`brutal-border p-3 text-left text-sm ${c.available ? "cursor-pointer" : "text-ink-muted cursor-not-allowed"} ${c.available && cloneProvider === c.id ? "bg-surface-inverse text-surface" : ""}`}
                            data-testid={`clone-provider-${c.id}`}>
                            <input type="radio" name="clone_provider" className="mr-2 align-middle"
                              value={c.id} checked={cloneProvider === c.id} disabled={!c.available}
                              onChange={() => setCloneProvider(c.id)} />
                            <span className="font-display font-bold">{c.label}</span>
                            {!c.available && <span className="block text-xs mt-1">Unavailable</span>}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  {cloningAvailable && cloneVoice && !cloneInfo && cloneProviders.length > 0 && !cloneProviders.some((c) => c.available) && (
                    <div className="text-xs mt-3 text-state-danger space-y-1" role="alert" data-testid="clone-unavailable">
                      {cloneProviders.map((c) => <p key={c.id}><b>{c.label}:</b> {c.reason}</p>)}
                    </div>
                  )}
                  {cloningAvailable && cloneVoice && !cloneInfo && cloneBlockedReason && cloneProviders.some((c) => c.available) && (
                    <p className="text-xs mt-3 text-state-danger" role="alert" data-testid="clone-blocked-reason">{cloneBlockedReason}</p>
                  )}
                  {cloningAvailable && cloneVoice && !cloneInfo && pickedClone?.needs_consent && (
                    <div className="brutal-card mt-4 bg-surface" data-testid="clone-consent">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" className="mt-1 w-4 h-4"
                          data-testid="clone-consent-check"
                          checked={cloneConsent}
                          onChange={(e) => setCloneConsent(e.target.checked)} />
                        <span className="text-xs leading-relaxed">
                          This is my own voice, or I hold the recorded permission of the person
                          speaking. I understand a voice model will be created and stored.
                        </span>
                      </label>
                      <label className="block mt-3">
                        <span className="brutal-label">Whose voice is this?</span>
                        <input type="text" className="brutal-input w-full mt-1"
                          data-testid="clone-consent-subject"
                          placeholder="Full name"
                          value={cloneSubject}
                          onChange={(e) => setCloneSubject(e.target.value)} />
                      </label>
                    </div>
                  )}
                  {cloningAvailable && cloneVoice && !cloneInfo && (
                    <button onClick={cloneVoiceNow}
                      disabled={cloning || !!cloneBlockedReason || (cloneProviders.length > 0 && !cloneProviders.some((c) => c.available)) || (pickedClone?.needs_consent && (!cloneConsent || !cloneSubject.trim()))}
                      className="brutal-btn mt-4" data-testid="btn-clone-voice">
                      {cloning ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Clone voice now
                    </button>
                  )}
                  {cloningAvailable && cloneInfo && (
                    <div className="brutal-card mt-4 bg-surface flex items-center justify-between gap-3" data-testid="clone-voice-status">
                      <div>
                        <div className="marquee-tag mb-1">// VOICE READY</div>
                        <div className="text-sm">
                          Cloned as <span className="font-mono-ui font-bold">{cloneInfo.name}</span> via {cloneInfo.provider}.
                          Voice ID <span className="font-mono-ui text-xs">{cloneInfo.voice_id?.slice(0,10)}…</span>
                        </div>
                        {cloneInfo.preview_url && (
                          <audio controls src={cloneInfo.preview_url} className="mt-2 w-full" data-testid="clone-preview" />
                        )}
                      </div>
                      <button onClick={async()=>{
                        try { await api.delete(`/projects/${projectId}/clone-voice`); setCloneInfo(null); setCloneVoice(false); toast.success("Cloned voice removed"); }
                        catch(e){ toast.error(errMsg(e, "Delete failed")); }
                      }} className="brutal-btn brutal-btn-ghost !p-2" data-testid="btn-uncline-voice" aria-label="Remove cloned voice"><X size={14} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="brutal-card">
              <div className="marquee-tag mb-3">// EXTRA TEXT (OPTIONAL)</div>
              <textarea data-testid="input-text" className="brutal-input min-h-[100px]" placeholder="Paste any additional context, feature list, or talking points..." value={form.text_input} onChange={(e)=>update("text_input",e.target.value)}/>
              <p className="text-xs text-ink-subtle uppercase tracking-widest mt-2">Saved on generate</p>
            </div>

            <div className="brutal-card">
              <div className="marquee-tag mb-3">// BRAND KIT (OPTIONAL) — STARTER / PRO</div>
              <label className="field-focusable brutal-btn brutal-btn-secondary cursor-pointer inline-flex" data-testid="btn-upload-logo">
                <ImageIcon size={16}/> Upload Logo (adds intro/outro slide)
                <input type="file" accept="image/*" className="sr-only" onChange={(e)=>{
                  const f = e.target.files?.[0]; if(f){ setLogoFile(f); uploadLogo(f); }
                }}/>
              </label>
              {logoFile && <span className="text-xs text-ink-muted uppercase tracking-widest ml-3">{logoFile.name}</span>}
            </div>
            </>
            )}

            <div className="flex justify-between">
              <button onClick={()=>setStep(1)} className="brutal-btn brutal-btn-secondary" data-testid="btn-back">Back</button>
              <button
                onClick={()=>setStep(3)}
                className="brutal-btn"
                data-testid="btn-step2-next"
                disabled={form.source_type === "screen_recording" && !screenBlob}
                title={form.source_type === "screen_recording" && !screenBlob ? "Record your screen first" : ""}
              >Next <ArrowRight size={16}/></button>
            </div>
          </div>
        )}

        {step===3 && (
          <div className="brutal-card" data-testid="step3-review">
            <div className="marquee-tag mb-3">// REVIEW</div>
            <h2 className="font-display font-black type-heading mb-4 tracking-tighter">{form.title}</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div><span className="brutal-label">Output</span>{form.kind}</div>
              <div><span className="brutal-label">Tone</span>{form.tone}</div>
              <div><span className="brutal-label">Voice</span>{form.voice}</div>
              <div><span className="brutal-label">Duration</span>{form.duration_target}s</div>
              <div><span className="brutal-label">Video Style</span>{form.video_style}</div>
              <div><span className="brutal-label">Aspect Ratio</span>{form.aspect_ratio}</div>
              <div><span className="brutal-label">Stock B-roll</span>{form.broll_mode==="stock" ? "On (free clips)" : "Off"}</div>
              <div><span className="brutal-label">Source</span>{form.source_type === "screen_recording" ? "Screen recording" : "Slideshow"}</div>
              {form.source_type === "screen_recording" ? (
                <div><span className="brutal-label">Recording</span>{screenBlob ? `${Math.round(screenBlob.size/1024/1024)} MB` : "MISSING"}</div>
              ) : (
                <>
                  <div><span className="brutal-label">Screenshots</span>{screenshots.length}</div>
                  <div><span className="brutal-label">Voice Memo</span>{audioBlob?"Yes":"None"}</div>
                </>
              )}
              <div><span className="brutal-label">Voiceover</span>{form.source_type === "screen_recording" ? (form.screen_audio_mode==="ai_voice" ? "AI re-narration (TTS)" : "From recording") : (form.voiceover_mode==="raw" ? "Raw recording (no TTS)" : "AI-generated")}</div>
              <div><span className="brutal-label">Captions</span>{form.caption_style==="off" ? "None" : (form.caption_style==="burn" ? "Burnt into video" : "Sidecar (SRT+VTT)")}</div>
              <div><span className="brutal-label">Language</span>{form.language}</div>
            </div>

            <div className="mt-6 pt-6 border-t brutal-border border-l-0 border-r-0 border-b-0" data-testid="music-picker">
              <div className="flex items-baseline justify-between mb-2">
                <div className="marquee-tag">// BACKGROUND MUSIC</div>
                <span className="text-2xs uppercase tracking-widest text-ink-subtle">Kevin MacLeod · CC BY 4.0</span>
              </div>
              <div className="grid md:grid-cols-4 gap-2">
                <label
                  data-testid="music-track-none"
                  className={`brutal-border p-3 cursor-pointer text-left ${!form.bg_music_key ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}
                >
                  <input
                    type="radio"
                    name="bg_music_key"
                    value=""
                    checked={!form.bg_music_key}
                    onChange={()=>update("bg_music_key","")}
                    className="mr-2 align-middle"
                  />
                  <span className="font-display font-black text-sm uppercase">None</span>
                  <div className="text-xs mt-1 opacity-80">Voice only. No music bed.</div>
                </label>
                {musicTracks.map(t => (
                  <label
                    key={t.key}
                    data-testid={`music-track-${t.key}`}
                    className={`brutal-border p-3 cursor-pointer text-left ${form.bg_music_key===t.key ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}
                  >
                    <input
                      type="radio"
                      name="bg_music_key"
                      value={t.key}
                      checked={form.bg_music_key===t.key}
                      onChange={()=>update("bg_music_key",t.key)}
                      className="mr-2 align-middle"
                    />
                    <span className="font-display font-black text-sm uppercase">{t.name}</span>
                    <div className="text-xs mt-1 opacity-80">Mood: {t.mood}{t.bpm ? ` · ${t.bpm} BPM` : ""}</div>
                    {t.artist && (
                      <div className="text-2xs mt-0.5 opacity-60 uppercase tracking-widest">by {t.artist}</div>
                    )}
                    <button
                      type="button"
                      onClick={(e)=>{
                        e.preventDefault();
                        if (musicPreviewKey === t.key) {
                          musicAudioRef.current?.pause();
                          setMusicPreviewKey(null);
                        } else {
                          if (musicAudioRef.current) musicAudioRef.current.pause();
                          const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${t.url}`);
                          a.play().catch(()=>{});
                          a.onended = ()=>setMusicPreviewKey(null);
                          musicAudioRef.current = a;
                          setMusicPreviewKey(t.key);
                        }
                      }}
                      className="text-2xs uppercase tracking-widest underline mt-2"
                      data-testid={`music-preview-${t.key}`}
                    >
                      {musicPreviewKey === t.key ? "◼ Stop" : "► Preview"}
                    </button>
                  </label>
                ))}
              </div>
              {form.bg_music_key && (
                <div className="mt-3 flex items-center gap-3" data-testid="music-volume-block">
                  <label className="brutal-label !mb-0 shrink-0">Volume</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={form.bg_music_volume}
                    onChange={(e)=>update("bg_music_volume", parseFloat(e.target.value))}
                    className="flex-1"
                    data-testid="music-volume-slider"
                  />
                  <span className="text-xs font-mono-ui tabular-nums w-12">{Math.round(form.bg_music_volume*100)}%</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t brutal-border border-l-0 border-r-0 border-b-0" data-testid="caption-picker">
              <div className="marquee-tag mb-2">// CAPTIONS</div>
              <div className="grid md:grid-cols-3 gap-2">
                {[
                  { v: "sidecar", label: "SRT + VTT sidecar", desc: "Standard. Toggle on/off in the video player. Also downloadable." },
                  { v: "burn",    label: "Burn into video",   desc: "Captions are baked into every frame. Great for social / muted autoplay." },
                  { v: "off",     label: "No captions",       desc: "Skip captions entirely." },
                ].map(opt => (
                  <label
                    key={opt.v}
                    data-testid={`caption-style-${opt.v}`}
                    className={`brutal-border p-3 cursor-pointer text-left ${form.caption_style===opt.v ? "bg-surface-inverse text-surface" : "hover:bg-surface"}`}
                  >
                    <input
                      type="radio"
                      name="caption_style"
                      value={opt.v}
                      checked={form.caption_style===opt.v}
                      onChange={()=>update("caption_style", opt.v)}
                      className="mr-2 align-middle"
                    />
                    <span className="font-display font-black text-sm uppercase">{opt.label}</span>
                    <div className="text-xs mt-1 leading-relaxed opacity-80">{opt.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t brutal-border border-l-0 border-r-0 border-b-0">
              <div className="flex items-center justify-between mb-3">
                <div className="marquee-tag">// LIVE SCRIPT PREVIEW (BETA)</div>
                <button type="button" onClick={streamPreview} disabled={streaming} className="brutal-btn brutal-btn-secondary" data-testid="btn-stream-preview">
                  <Sparkles size={14}/> {streaming?"Streaming…":"Preview Script"}
                </button>
              </div>
              {(streamText || streaming) && (
                <>
                  <textarea
                    className="brutal-input min-h-[160px] mt-2 font-mono-ui text-sm"
                    value={streamText}
                    onChange={(e)=>setStreamText(e.target.value)}
                    data-testid="stream-preview"
                    placeholder="Streaming…"
                  />
                  <div className="flex gap-2 mt-2">
                    <button type="button" onClick={saveEditedScript} className="brutal-btn brutal-btn-secondary" data-testid="btn-save-script">Save edited script</button>
                    <span className="text-xs text-ink-muted uppercase tracking-widest self-center">Saved scripts are used verbatim on generate — no re-write, saves credits.</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button onClick={()=>setStep(2)} className="brutal-btn brutal-btn-secondary" data-testid="btn-back-2">Back</button>
              <button onClick={startGeneration} className="brutal-btn" data-testid="btn-generate">
                Generate <ArrowRight size={16}/>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
